import { THEME_PREFERENCES, useThemeStore, type ThemePreference } from '../stores/themeStore'
import './AppearanceSwitch.css'

const LABELS: Record<ThemePreference, string> = { light: 'Light', dark: 'Dark', system: 'System' }

/** The website's outline icons (Website/fursahub/components/WebApp/Shell/ShellIcon.tsx). */
const ICONS: Record<ThemePreference, JSX.Element> = {
  light: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4" />
    </>
  ),
  dark: <path d="M20.5 14.5A8.5 8.5 0 1 1 9.5 3.5a7 7 0 0 0 11 11z" />,
  system: (
    <>
      <rect x="3" y="4" width="18" height="12.5" rx="2" />
      <path d="M8.5 20.5h7M12 16.5v4" />
    </>
  ),
}

/** One of the website's outline icons for a theme choice. */
export function ThemeIcon({ option, size = 14 }: { option: ThemePreference | 'dark'; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICONS[option]}
    </svg>
  )
}

/**
 * Light / Dark / System, as the website's AppearanceSwitch: a real radio group,
 * so arrow keys and screen readers work as usual; picking one applies it at once.
 *
 * - `compact`: a small segmented pill (under the sign-in card).
 * - `list`: full-width rows with an icon, label and radio (the side menu's
 *   More → Switch appearance), "System" with a hint.
 */
export function AppearanceSwitch({ variant = 'compact' }: { variant?: 'compact' | 'list' }) {
  const preference = useThemeStore((s) => s.preference)
  const setPreference = useThemeStore((s) => s.setPreference)
  const list = variant === 'list'

  return (
    <form className={list ? 'fh-appearance-list' : 'fh-appearance'} onSubmit={(e) => e.preventDefault()}>
      <fieldset>
        <legend className="fh-sr-only">Appearance</legend>
        <div className="fh-appearance-options">
          {THEME_PREFERENCES.map((option) => (
            <label key={option} className="fh-appearance-option" title={list ? undefined : LABELS[option]}>
              <input
                type="radio"
                name={list ? 'fh-theme-list' : 'fh-theme'}
                value={option}
                checked={preference === option}
                onChange={() => setPreference(option)}
                className={list ? 'fh-appearance-radio' : 'fh-sr-only'}
              />
              <ThemeIcon option={option} size={list ? 20 : 14} />
              <span className="fh-appearance-text">
                <span>{LABELS[option]}</span>
                {list && option === 'system' && (
                  <span className="fh-appearance-hint">Follows your device's setting</span>
                )}
              </span>
            </label>
          ))}
        </div>
      </fieldset>
    </form>
  )
}
